/**
 * PortfolioCombinedViews — Iter Feb 2026 R47.
 *
 * Renders the four combined tabs (Tasks, Milestones, Gantt, Activity Log)
 * for a portfolio, plus a `QuickCommentDialog` used from the Projects tab.
 *
 * Kept intentionally simple / read-only. The Gantt view is a compact
 * CSS bar timeline (no external Gantt library) with a
 * tasks+milestones toggle.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  MessageSquarePlus, AlertTriangle, CheckCircle2, Flag, Activity, Loader2,
  ExternalLink, Filter, ChevronDown, ChevronRight,
} from "lucide-react";
// R138v · Feb 2026 — Full task-editor popup right inside Executive
// Portfolio. Mounts the entire <Tasks /> module in embedded mode scoped
// to a single task id, so directors can edit description, subtasks,
// checklists, attachments, comments, time entries — the whole shebang —
// without leaving the portfolio page.
import Tasks from "@/pages/app/Tasks";
import { QuickAddTaskPopover } from "@/components/tasks/QuickAddTask";

const STATUS_STYLE = {
  todo:        "bg-neutral-100 text-neutral-700 border-neutral-300",
  in_progress: "bg-sky-100 text-sky-700 border-sky-300",
  review:      "bg-violet-100 text-violet-700 border-violet-300",
  blocked:     "bg-rose-100 text-rose-700 border-rose-300",
  done:        "bg-emerald-100 text-emerald-700 border-emerald-300",
  cancelled:   "bg-neutral-100 text-neutral-500 border-neutral-300",
  planned:     "bg-neutral-100 text-neutral-700 border-neutral-300",
  at_risk:     "bg-amber-100 text-amber-800 border-amber-300",
};

function statusPill(s) {
  return (
    <span className={`text-[10px] px-1.5 py-0.5 border uppercase ${STATUS_STYLE[s] || STATUS_STYLE.todo}`}>
      {(s || "").replace("_", " ") || "—"}
    </span>
  );
}


// R138v · Feb 2026 — Task editor dialog for Executive Portfolio.
//
// Mounts the entire `<Tasks />` module in embedded mode scoped to a
// single task id. The Tasks module auto-focuses the first row when
// `initialTaskIds` contains exactly one id — which opens the full
// TaskDetail edit popup — so the user gets the identical editing
// experience as the standalone Tasks module (description, subtasks,
// checklist, attachments, comments, time entries, links). No new UI to
// maintain here.
export function PortfolioTaskEditDialog({ taskId, projectName, onClose, onAddTask }) {
  if (!taskId) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-[96vw] w-[96vw] h-[92vh] p-0 gap-0 overflow-hidden flex flex-col"
        data-testid="portfolio-task-edit-dialog"
      >
        <DialogHeader className="px-3 sm:px-6 py-3 border-b border-border flex-shrink-0">
          <DialogTitle className="text-sm sm:text-base flex items-center gap-2">
            <span className="label-eyebrow text-[10px]">Task edit</span>
            <span className="truncate">{projectName || "Task"}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto" data-testid="portfolio-task-edit-body">
          <Tasks embedded initialTaskIds={[taskId]} onAddTaskShortcut={onAddTask} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Combined Tasks ────────────────────────────────────────────────
export function CombinedTasksView({ portfolioId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("open"); // "all"|"open"|"overdue"|"done"
  const nav = useNavigate();
  const { activeWorkspaceId } = useAuth();
  // R102 · Peek an individual task inline instead of navigating away, and
  // quick-add a task straight from the exec view. The QuickAddTaskPopover
  // needs the workspace's project list to power its project picker.
  const [peekTask, setPeekTask] = useState(null);
  const [quickAddProject, setQuickAddProject] = useState(null);
  const [projects, setProjects] = useState([]);

  const reload = () => setStatusFilter((s) => s); // force effect re-run

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (statusFilter === "open") params.status = "todo,in_progress,review,blocked";
        if (statusFilter === "done") params.status = "done";
        if (statusFilter === "overdue") params.overdue_only = true;
        const { data } = await api.get(`/portfolios/${portfolioId}/combined/tasks`, { params });
        if (!cancel) setItems(data.items || []);
      } catch (e) { if (!cancel) toast.error(apiErrorMessage(e, "Failed to load tasks")); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [portfolioId, statusFilter]);

  // Load workspace projects once for the QuickAddTaskPopover dropdown.
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/projects`)
      .then((r) => setProjects(r.data || []))
      .catch(() => setProjects([]));
  }, [activeWorkspaceId]);

  // Derive the project-set actually present in this portfolio so the quick-
  // add picker only shows projects the exec is viewing.
  const portfolioProjects = useMemo(() => {
    const ids = new Set(items.map((t) => t.project_id).filter(Boolean));
    return projects.filter((p) => ids.has(p.id));
  }, [items, projects]);

  return (
    <div className="space-y-3" data-testid="portfolio-combined-tasks">
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-3.5 h-3.5 text-muted-foreground" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 rounded-none w-40 text-xs" data-testid="combined-tasks-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open only</SelectItem>
            <SelectItem value="overdue">Overdue only</SelectItem>
            <SelectItem value="done">Done only</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground">{items.length} task{items.length === 1 ? "" : "s"}</div>
        {/* R102 · Quick-add without leaving the exec view. */}
        <div className="ml-auto">
          <QuickAddTaskPopover
            wsId={activeWorkspaceId}
            projects={portfolioProjects.length ? portfolioProjects : projects}
            onCreated={reload}
            testid="combined-tasks-quickadd"
          />
        </div>
      </div>
      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
       : items.length === 0 ? <div className="border p-6 text-center text-sm text-muted-foreground">No tasks matching the filter.</div> : (
        <div className="border">
          <table className="w-full text-xs">
            <thead className="bg-muted/30">
              <tr className="text-left">
                <th className="p-2">Task</th>
                <th className="p-2">Project</th>
                <th className="p-2">Assignee</th>
                <th className="p-2">Status</th>
                <th className="p-2">Due</th>
                <th className="p-2 w-24 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} className="border-t border-border hover:bg-muted/20 cursor-pointer" onClick={() => setPeekTask(t)} data-testid={`combined-task-row-${t.id}`}>
                  <td className="p-2 font-medium truncate max-w-[280px]">{t.title}</td>
                  <td className="p-2 text-muted-foreground truncate">{t.project_name || t.project_id?.slice(0, 8)}</td>
                  <td className="p-2 text-muted-foreground">{t.assignee_name || "—"}</td>
                  <td className="p-2">{statusPill(t.status)}</td>
                  <td className={`p-2 tabular-nums ${t._overdue ? "text-rose-700 font-semibold" : "text-muted-foreground"}`}>{t.due_date || "—"}</td>
                  <td className="p-2 text-right">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const proj = projects.find((p) => p.id === t.project_id);
                        setQuickAddProject({ id: t.project_id, name: t.project_name || proj?.name || "Project", at: Date.now() });
                      }}
                      className="text-primary hover:underline text-[10px] uppercase tracking-widest mr-2"
                      title="Add another task to this project"
                      data-testid={`combined-task-add-sibling-${t.id}`}
                    >
                      + task
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); nav(`/app/projects/${t.project_id}?task=${t.id}`); }}
                      className="text-muted-foreground hover:text-foreground"
                      title="Open in Projects"
                      data-testid={`combined-task-open-full-${t.id}`}
                    >
                      <ExternalLink className="w-3 h-3 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* R138v · Full editor for any clicked task — no more read-only
          peek. Directors can edit right from the portfolio view. */}
      {peekTask && (
        <PortfolioTaskEditDialog
          taskId={peekTask.id}
          projectName={peekTask.project_name}
          onClose={() => setPeekTask(null)}
          onAddTask={() => {
            const cache = peekTask;
            setPeekTask(null);
            setQuickAddProject({ id: cache.project_id, name: cache.project_name, at: Date.now() });
          }}
        />
      )}
      {/* R102 · Sibling-add popover — QuickAddTaskPopover fires open via
          `defaultOpen` because `at` bumps whenever the user clicks + task. */}
      {quickAddProject && (
        <QuickAddTaskPopover
          key={quickAddProject.at}
          wsId={activeWorkspaceId}
          projects={projects}
          preset={{ project_id: quickAddProject.id }}
          defaultOpen
          onOpenChange={(v) => { if (!v) setQuickAddProject(null); }}
          onCreated={() => { setQuickAddProject(null); reload(); }}
          testid="combined-tasks-sibling-quickadd"
        />
      )}
    </div>
  );
}

// ── Combined Milestones ───────────────────────────────────────────
export function CombinedMilestonesView({ portfolioId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("open");
  const [openMilestone, setOpenMilestone] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (statusFilter === "open") params.status = "planned,in_progress,at_risk,blocked";
        if (statusFilter === "done") params.status = "done";
        const { data } = await api.get(`/portfolios/${portfolioId}/combined/milestones`, { params });
        if (!cancel) {
          let rows = data.items || [];
          if (statusFilter === "overdue") rows = rows.filter(m => m._overdue);
          setItems(rows);
        }
      } catch (e) { if (!cancel) toast.error(apiErrorMessage(e, "Failed to load milestones")); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [portfolioId, statusFilter, refreshKey]);

  return (
    <div className="space-y-3" data-testid="portfolio-combined-milestones">
      <div className="flex items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-muted-foreground" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 rounded-none w-40 text-xs" data-testid="combined-milestones-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="done">Done</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground ml-auto">{items.length} milestone{items.length === 1 ? "" : "s"}</div>
      </div>
      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
       : items.length === 0 ? <div className="border p-6 text-center text-sm text-muted-foreground">No milestones matching the filter.</div> : (
        <div className="border">
          <table className="w-full text-xs">
            <thead className="bg-muted/30">
              <tr className="text-left">
                <th className="p-2">Milestone</th>
                <th className="p-2">Project</th>
                <th className="p-2">Status</th>
                <th className="p-2">Due</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id} className="border-t border-border hover:bg-muted/20 cursor-pointer" onClick={() => setOpenMilestone(m)} data-testid={`combined-milestone-row-${m.id}`}>
                  <td className="p-2 font-medium truncate flex items-center gap-1"><Flag className="w-3 h-3 text-sky-600" />{m.title}</td>
                  <td className="p-2 text-muted-foreground truncate">{m.project_name || m.project_id?.slice(0, 8)}</td>
                  <td className="p-2">{statusPill(m.status)}</td>
                  <td className={`p-2 tabular-nums ${m._overdue ? "text-rose-700 font-semibold" : "text-muted-foreground"}`}>{m.due_date || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* R138w · Milestone details dialog. Milestones don't have a
          dedicated editor in the CRM (they live inside a project's
          Milestones tab), so this dialog shows the record with a jump-
          to-project CTA plus the milestone's associated tasks. */}
      {openMilestone && (
        <MilestonePeekDialog milestone={openMilestone}
                                          onClose={() => setOpenMilestone(null)}
                                          onSaved={() => {
                                            // Refetch the milestone list so the row reflects the edit.
                                            setStatusFilter((s) => s);
                                            // Nudge the effect with a no-op state change by
                                            // toggling a hidden refresh key.
                                            setRefreshKey((k) => k + 1);
                                          }}
                                          onOpenInProject={() => {
                                            nav(`/app/projects/${openMilestone.project_id}?tab=milestones`);
                                          }} />
      )}
    </div>
  );
}


function MilestonePeekDialog({ milestone, onClose, onOpenInProject, onSaved }) {
  const [title, setTitle] = useState(milestone.title || "");
  const [status, setStatus] = useState(milestone.status || "planned");
  const [dueDate, setDueDate] = useState(milestone.due_date || "");
  const [startDate, setStartDate] = useState(milestone.start_date || "");
  const [description, setDescription] = useState(milestone.description || "");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const originalRef = useMemo(() => ({
    title: milestone.title || "", status: milestone.status || "planned",
    due_date: milestone.due_date || "", start_date: milestone.start_date || "",
    description: milestone.description || "",
  }), [milestone]);
  const markDirty = () => setDirty(true);

  const save = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const patch = {};
      if (title !== originalRef.title) patch.title = title.trim();
      if (status !== originalRef.status) patch.status = status;
      if (dueDate !== originalRef.due_date) patch.due_date = dueDate;
      if (startDate !== originalRef.start_date) patch.start_date = startDate;
      if (description !== originalRef.description) patch.description = description;
      if (Object.keys(patch).length === 0) {
        toast.success("Nothing to save");
        onClose?.();
        return;
      }
      await api.patch(`/milestones/${milestone.id}`, patch);
      toast.success("Milestone updated");
      setDirty(false);
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => {
      if (v) return;
      if (dirty && !window.confirm("Discard unsaved changes?")) return;
      onClose?.();
    }}>
      <DialogContent className="max-w-lg" data-testid="milestone-peek-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Flag className="w-4 h-4 text-sky-600" /> Edit milestone
          </DialogTitle>
          <DialogDescription>{milestone.project_name || "Milestone"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Title *</label>
            <Input value={title}
                       onChange={(e) => { setTitle(e.target.value); markDirty(); }}
                       className="rounded-none"
                       data-testid="milestone-edit-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Status</label>
              <Select value={status} onValueChange={(v) => { setStatus(v); markDirty(); }}>
                <SelectTrigger className="h-9 rounded-none" data-testid="milestone-edit-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="at_risk">At risk</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Due date</label>
              <Input type="date" value={dueDate}
                         onChange={(e) => { setDueDate(e.target.value); markDirty(); }}
                         className="rounded-none"
                         data-testid="milestone-edit-due" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Start date</label>
              <Input type="date" value={startDate}
                         onChange={(e) => { setStartDate(e.target.value); markDirty(); }}
                         className="rounded-none"
                         data-testid="milestone-edit-start" />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Description</label>
            <Textarea value={description}
                              onChange={(e) => { setDescription(e.target.value); markDirty(); }}
                              rows={3}
                              className="rounded-none text-xs"
                              placeholder="Owners, deliverables, dependencies…"
                              data-testid="milestone-edit-description" />
          </div>
        </div>
        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button variant="ghost" className="rounded-none mr-auto"
                    onClick={onOpenInProject}
                    data-testid="milestone-peek-open">
            Open in project <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
          </Button>
          <Button variant="outline" className="rounded-none"
                    onClick={onClose}
                    data-testid="milestone-peek-close">Cancel</Button>
          <Button className="rounded-none"
                    onClick={save}
                    disabled={busy || !dirty}
                    data-testid="milestone-peek-save">
            {busy ? "Saving…" : dirty ? "Save changes" : "No changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Combined Log ──────────────────────────────────────────────────
export function CombinedLogView({ portfolioId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState("all"); // all|activity|status_update

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/portfolios/${portfolioId}/combined/log?limit=300`);
        if (!cancel) setItems(data.items || []);
      } catch (e) { if (!cancel) toast.error(apiErrorMessage(e, "Failed to load log")); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [portfolioId]);

  const filtered = useMemo(() => kindFilter === "all" ? items : items.filter(e => e.kind === kindFilter), [items, kindFilter]);

  return (
    <div className="space-y-3" data-testid="portfolio-combined-log">
      <div className="flex items-center gap-2">
        <Activity className="w-3.5 h-3.5 text-muted-foreground" />
        <Select value={kindFilter} onValueChange={setKindFilter}>
          <SelectTrigger className="h-8 rounded-none w-40 text-xs" data-testid="combined-log-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All events</SelectItem>
            <SelectItem value="activity">Activity only</SelectItem>
            <SelectItem value="status_update">Comments/updates</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground ml-auto">{filtered.length} event{filtered.length === 1 ? "" : "s"}</div>
      </div>
      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
       : filtered.length === 0 ? <div className="border p-6 text-center text-sm text-muted-foreground">No events found.</div> : (
        <div className="border divide-y">
          {filtered.map((e, i) => (
            <div key={(e.id || i) + e.kind} className="p-2 text-xs flex items-start gap-2" data-testid={`combined-log-row-${i}`}>
              <div className="mt-0.5">
                {e.kind === "activity" ? <Activity className="w-3.5 h-3.5 text-muted-foreground" /> : <MessageSquarePlus className="w-3.5 h-3.5 text-sky-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
                  <span className="font-medium text-foreground">{e.actor || "System"}</span>
                  <span>·</span>
                  <span className="uppercase tracking-widest">{e.kind === "activity" ? "activity" : (e.post_kind || e.kind)}</span>
                  {e.visibility && <span className="border px-1">{e.visibility}</span>}
                  <span className="ml-auto">{(e.at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="mt-0.5">{e.text}</div>
                {e.project_name && <div className="text-[10px] text-muted-foreground mt-0.5">on {e.project_name}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Combined Gantt (with date-header ruler + zoom levels) ─────────
// R138y · Feb 2026 — Full zoom levels (Day / Week / Month / Quarter /
// Year) + two-tier date header (primary tier for the current zoom,
// secondary tier for the containing period), rich hover cards, and a
// horizontal-scrollable timeline so directors can navigate long
// horizons without losing project labels.
const ZOOM = {
  day:     { pxPerDay: 32, primary: "day",     secondary: "month",  primaryLabel: (d) => String(d.getDate()) },
  week:    { pxPerDay: 12, primary: "week",    secondary: "month",  primaryLabel: (d) => `W${_isoWeek(d)}` },
  month:   { pxPerDay: 6,  primary: "month",   secondary: "quarter", primaryLabel: (d) => d.toLocaleString("en", { month: "short" }) },
  quarter: { pxPerDay: 2,  primary: "quarter", secondary: "year",   primaryLabel: (d) => `Q${Math.floor(d.getMonth() / 3) + 1}` },
  year:    { pxPerDay: 0.7, primary: "year",   secondary: null,     primaryLabel: (d) => String(d.getFullYear()) },
};

function _isoWeek(d) {
  const target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNr = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNr + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const diff = (target - firstThursday) / 86400000;
  return 1 + Math.round(diff / 7);
}

function _addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function _iso(d) { return d.toISOString().slice(0, 10); }
function _startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function _startOfQuarter(d) {
  const q = Math.floor(d.getMonth() / 3);
  return new Date(d.getFullYear(), q * 3, 1);
}
function _startOfYear(d) { return new Date(d.getFullYear(), 0, 1); }
function _startOfWeek(d) { const x = new Date(d); const dow = (x.getDay() + 6) % 7; x.setDate(x.getDate() - dow); return x; }

function _nextTick(d, unit) {
  const x = new Date(d);
  if (unit === "day")     x.setDate(x.getDate() + 1);
  if (unit === "week")    x.setDate(x.getDate() + 7);
  if (unit === "month")   x.setMonth(x.getMonth() + 1);
  if (unit === "quarter") x.setMonth(x.getMonth() + 3);
  if (unit === "year")    x.setFullYear(x.getFullYear() + 1);
  return x;
}

function _tickStart(d, unit) {
  if (unit === "day")     { const x = new Date(d); x.setHours(0,0,0,0); return x; }
  if (unit === "week")    return _startOfWeek(d);
  if (unit === "month")   return _startOfMonth(d);
  if (unit === "quarter") return _startOfQuarter(d);
  if (unit === "year")    return _startOfYear(d);
  return d;
}


export function CombinedGanttView({ portfolioId }) {
  const [tasks, setTasks] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState("both");
  const [zoom, setZoom] = useState(() => {
    try { return localStorage.getItem(`pf-gantt-zoom:${portfolioId}`) || "week"; }
    catch { return "week"; }
  });
  const [groupBy, setGroupBy] = useState(() => {
    try { return localStorage.getItem(`pf-gantt-group:${portfolioId}`) || "none"; }
    catch { return "none"; }
  });
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const [openTaskId, setOpenTaskId] = useState(null);
  const [openMilestone, setOpenMilestone] = useState(null);
  const [hover, setHover] = useState(null);
  const [dragState, setDragState] = useState(null); // { bar, mode: "move"|"end", startX, origStart, origEnd }
  const scrollRef = useRef(null);
  const didAutoScrollRef = useRef(false);
  const nav = useNavigate();

  useEffect(() => {
    try { localStorage.setItem(`pf-gantt-zoom:${portfolioId}`, zoom); } catch { /* noop */ }
  }, [portfolioId, zoom]);
  useEffect(() => {
    try { localStorage.setItem(`pf-gantt-group:${portfolioId}`, groupBy); } catch { /* noop */ }
  }, [portfolioId, groupBy]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const [tRes, mRes] = await Promise.all([
          api.get(`/portfolios/${portfolioId}/combined/tasks?status=todo,in_progress,review,blocked,done`),
          api.get(`/portfolios/${portfolioId}/combined/milestones`),
        ]);
        if (cancel) return;
        setTasks(tRes.data.items || []);
        setMilestones(mRes.data.items || []);
      } catch (e) { if (!cancel) toast.error(apiErrorMessage(e, "Failed to load Gantt data")); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [portfolioId]);

  const bars = useMemo(() => {
    const rows = [];
    if (show === "tasks" || show === "both") {
      for (const t of tasks) {
        if (!t.due_date) continue;
        const start = t.start_date || t.created_at?.slice(0, 10) || t.due_date;
        rows.push({
          id: `t-${t.id}`, raw_id: t.id, kind: "task", title: t.title,
          project_name: t.project_name, start, end: t.due_date,
          status: t.status, project_id: t.project_id, overdue: t._overdue,
          assignee_name: t.assignee_name, priority: t.priority,
        });
      }
    }
    if (show === "milestones" || show === "both") {
      for (const m of milestones) {
        if (!m.due_date) continue;
        rows.push({
          id: `m-${m.id}`, raw_id: m.id, kind: "milestone", title: m.title,
          project_name: m.project_name, start: m.start_date || m.due_date,
          end: m.due_date, status: m.status, project_id: m.project_id,
          overdue: m._overdue,
        });
      }
    }
    rows.sort((a, b) => (a.start || "").localeCompare(b.start || ""));
    return rows;
  }, [tasks, milestones, show]);

  // Compute domain with padding so the ruler extends a bit past the last bar.
  const [domainStart, domainEnd] = useMemo(() => {
    if (!bars.length) return [new Date(), new Date()];
    let a = new Date(bars[0].start);
    let b = new Date(bars[0].end);
    for (const r of bars) {
      const s = new Date(r.start), e = new Date(r.end);
      if (s < a) a = s;
      if (e > b) b = e;
    }
    // Snap to tick boundaries so headers align cleanly.
    const cfg = ZOOM[zoom];
    return [_tickStart(a, cfg.primary), _nextTick(_tickStart(b, cfg.primary), cfg.primary)];
  }, [bars, zoom]);

  const cfg = ZOOM[zoom];
  const totalDays = Math.max(1, Math.round((domainEnd - domainStart) / 86400000));
  const timelinePx = totalDays * cfg.pxPerDay;
  const px = (d) => {
    const diff = (new Date(d) - domainStart) / 86400000;
    return Math.max(0, Math.min(timelinePx, diff * cfg.pxPerDay));
  };

  // Build ruler ticks.
  const primaryTicks = useMemo(() => {
    const out = [];
    let cursor = _tickStart(domainStart, cfg.primary);
    while (cursor < domainEnd) {
      const next = _nextTick(cursor, cfg.primary);
      out.push({ start: new Date(cursor), end: next < domainEnd ? new Date(next) : new Date(domainEnd), label: cfg.primaryLabel(cursor) });
      cursor = next;
    }
    return out;
  }, [domainStart, domainEnd, cfg]);

  const secondaryTicks = useMemo(() => {
    if (!cfg.secondary) return [];
    const out = [];
    let cursor = _tickStart(domainStart, cfg.secondary);
    while (cursor < domainEnd) {
      const next = _nextTick(cursor, cfg.secondary);
      let label = "";
      if (cfg.secondary === "month")   label = cursor.toLocaleString("en", { month: "short", year: "numeric" });
      if (cfg.secondary === "quarter") label = `Q${Math.floor(cursor.getMonth() / 3) + 1} ${cursor.getFullYear()}`;
      if (cfg.secondary === "year")    label = String(cursor.getFullYear());
      out.push({ start: new Date(cursor), end: next < domainEnd ? new Date(next) : new Date(domainEnd), label });
      cursor = next;
    }
    return out;
  }, [domainStart, domainEnd, cfg]);

  const todayLeft = useMemo(() => {
    const now = new Date();
    if (now < domainStart || now > domainEnd) return null;
    return px(_iso(now));
  }, [domainStart, domainEnd]);

  // R138aa · Feb 2026 — Auto-scroll the timeline so today's line lands
  // roughly at 30% from the left on the first render. Re-triggers when
  // zoom changes so the highlight stays centred.
  useEffect(() => {
    if (didAutoScrollRef.current) return;
    if (!scrollRef.current || todayLeft == null) return;
    const container = scrollRef.current;
    const target = Math.max(0, todayLeft - container.clientWidth * 0.3);
    container.scrollTo({ left: target, behavior: "smooth" });
    didAutoScrollRef.current = true;
  }, [todayLeft]);

  // R138aa · Feb 2026 — Group-by-project bucketing. Each group row is a
  // synthetic bar spanning the earliest → latest of its children so
  // scanning 100+ tasks stays sane. Groups can collapse to hide children.
  const grouped = useMemo(() => {
    if (groupBy !== "project") return null;
    const byProject = new Map();
    for (const r of bars) {
      const key = r.project_id || "_none_";
      if (!byProject.has(key)) {
        byProject.set(key, {
          project_id: key,
          project_name: r.project_name || "Unassigned",
          bars: [],
        });
      }
      byProject.get(key).bars.push(r);
    }
    const groups = Array.from(byProject.values()).map((g) => {
      // Compute earliest start + latest end for group summary bar.
      let s = g.bars[0].start, e = g.bars[0].end;
      let done = 0, overdue = 0;
      for (const b of g.bars) {
        if (b.start < s) s = b.start;
        if (b.end > e) e = b.end;
        if (b.status === "done") done += 1;
        if (b.overdue) overdue += 1;
      }
      return { ...g, start: s, end: e, done, overdue, total: g.bars.length };
    });
    groups.sort((a, b) => (a.start || "").localeCompare(b.start || ""));
    return groups;
  }, [bars, groupBy]);

  const toggleGroup = (pid) => {
    setCollapsedGroups((s) => {
      const next = new Set(s);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };

  // R138aa · Drag-to-reschedule. Two modes:
  //   - "move"  ← drag from centre of the bar → shifts both start + due
  //   - "end"   ← drag the right handle → only changes due
  // Dates snap to whole days. Persists via PATCH /tasks/{id}. Only tasks
  // are draggable — milestones just show a hover cursor since backend
  // rescheduling would need to cascade linked tasks.
  const dragBar = async (opts) => {
    const { bar, mode, e, rowEl } = opts;
    if (bar.kind !== "task") return;
    e.preventDefault();
    e.stopPropagation();
    const rect = rowEl.getBoundingClientRect();
    // We ignore the label-gutter width because the row is the timeline area.
    const startX = e.clientX;
    const origStart = new Date(bar.start);
    const origEnd = new Date(bar.end);
    setDragState({ bar, mode });
    const onMove = (ev) => {
      const deltaDays = Math.round((ev.clientX - startX) / cfg.pxPerDay);
      if (!Number.isFinite(deltaDays)) return;
      const previewEl = document.querySelector(`[data-drag-bar="${bar.id}"]`);
      if (!previewEl) return;
      if (mode === "move") {
        const newLeft = px(_iso(_addDays(origStart, deltaDays)));
        previewEl.style.left = `${newLeft}px`;
      } else if (mode === "end") {
        const newEnd = _addDays(origEnd, deltaDays);
        const newWidth = Math.max(2, px(_iso(newEnd)) - px(bar.start));
        previewEl.style.width = `${newWidth}px`;
      }
    };
    const onUp = async (ev) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const deltaDays = Math.round((ev.clientX - startX) / cfg.pxPerDay);
      setDragState(null);
      if (!deltaDays) return;
      try {
        if (mode === "move") {
          const newStart = _iso(_addDays(origStart, deltaDays));
          const newDue = _iso(_addDays(origEnd, deltaDays));
          await api.patch(`/tasks/${bar.raw_id}`, { start_date: newStart, due_date: newDue });
          toast.success(`Rescheduled → ${newStart} → ${newDue}`);
          setTasks((prev) => prev.map((t) => t.id === bar.raw_id ? { ...t, start_date: newStart, due_date: newDue } : t));
        } else if (mode === "end") {
          const newDue = _iso(_addDays(origEnd, deltaDays));
          await api.patch(`/tasks/${bar.raw_id}`, { due_date: newDue });
          toast.success(`Due → ${newDue}`);
          setTasks((prev) => prev.map((t) => t.id === bar.raw_id ? { ...t, due_date: newDue } : t));
        }
      } catch (err) { toast.error(apiErrorMessage(err, "Reschedule failed")); }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const LABEL_W = 260;

  return (
    <div className="space-y-3" data-testid="portfolio-combined-gantt">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex border" role="tablist" data-testid="gantt-toggle">
          {[{ k: "both", l: "T + M", lFull: "Tasks + Milestones" }, { k: "tasks", l: "Tasks", lFull: "Tasks only" }, { k: "milestones", l: "MS", lFull: "Milestones only" }].map(o => (
            <button key={o.k} className={`px-2 h-8 text-xs border-r last:border-r-0 ${show === o.k ? "bg-foreground text-background" : "hover:bg-muted"}`} onClick={() => setShow(o.k)} data-testid={`gantt-toggle-${o.k}`}>
              <span className="sm:hidden">{o.l}</span><span className="hidden sm:inline">{o.lFull}</span>
            </button>
          ))}
        </div>
        <div className="inline-flex border" role="tablist" data-testid="gantt-zoom">
          <span className="hidden sm:flex px-2 h-8 text-[10px] uppercase tracking-widest text-muted-foreground items-center border-r">Zoom</span>
          {Object.keys(ZOOM).map((k) => (
            <button key={k}
                      className={`px-2 h-8 text-xs border-r last:border-r-0 capitalize ${zoom === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                      onClick={() => setZoom(k)}
                      data-testid={`gantt-zoom-${k}`}>{k.slice(0, 1).toUpperCase() + (k.length > 1 ? k.slice(1) : "")}</button>
          ))}
        </div>
        <div className="inline-flex border" role="tablist" data-testid="gantt-group">
          <span className="hidden sm:flex px-2 h-8 text-[10px] uppercase tracking-widest text-muted-foreground items-center border-r">Group</span>
          {[{ k: "none", l: "Flat" }, { k: "project", l: "Project" }].map((o) => (
            <button key={o.k}
                      className={`px-2 h-8 text-xs border-r last:border-r-0 ${groupBy === o.k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                      onClick={() => setGroupBy(o.k)}
                      data-testid={`gantt-group-${o.k}`}>{o.l}</button>
          ))}
        </div>
        {bars.length > 0 && (
          <div className="text-[10px] sm:text-xs text-muted-foreground sm:ml-auto w-full sm:w-auto">
            {_iso(domainStart)} → {_iso(domainEnd)} · {bars.length} bars
          </div>
        )}
      </div>
      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
       : !bars.length ? <div className="border p-6 text-center text-sm text-muted-foreground">Nothing to show. Add due-dates on tasks or milestones to populate the Gantt.</div> : (
        <div className="border overflow-hidden relative">
          <div ref={scrollRef} className="overflow-x-auto max-h-[70vh] overflow-y-auto" data-testid="gantt-scroll">
            <div style={{ minWidth: LABEL_W + timelinePx }}>
              {/* Ruler header — 2 tiers, sticky at top */}
              <div className="sticky top-0 z-10 bg-background border-b border-border" data-testid="gantt-ruler">
                {cfg.secondary && (
                  <div className="flex text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/40 border-b border-border">
                    <div style={{ width: LABEL_W }} className="px-2 py-1 border-r border-border shrink-0">Timeline</div>
                    {secondaryTicks.map((t, i) => {
                      const left = px(_iso(t.start));
                      const width = Math.max(1, px(_iso(t.end)) - left);
                      return (
                        <div key={`s-${i}`}
                                style={{ width, paddingLeft: 6 }}
                                className="py-1 border-r border-border font-semibold text-foreground/80 truncate shrink-0">
                          {t.label}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="flex text-[10px] text-muted-foreground bg-background">
                  <div style={{ width: LABEL_W }} className="px-2 py-1 border-r border-border shrink-0" />
                  {primaryTicks.map((t, i) => {
                    const left = px(_iso(t.start));
                    const width = Math.max(1, px(_iso(t.end)) - left);
                    return (
                      <div key={`p-${i}`}
                              style={{ width, paddingLeft: 4 }}
                              className="py-1 border-r border-border/50 truncate shrink-0">
                        {t.label}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Bar rows — flat OR grouped by project */}
              {(() => {
                const renderBarRow = (r, groupKeyPrefix = "") => {
                  const left = px(r.start);
                  const width = Math.max(2, px(r.end) - left);
                  const barCls = r.kind === "milestone"
                    ? "bg-sky-200 border-sky-500"
                    : r.overdue ? "bg-rose-200 border-rose-500"
                    : r.status === "done" ? "bg-emerald-200 border-emerald-500"
                    : "bg-indigo-200 border-indigo-500";
                  return (
                    <div key={`${groupKeyPrefix}${r.id}`}
                            className="flex items-stretch border-b hover:bg-muted/20"
                            data-testid={`gantt-bar-${r.id}`}>
                      <div style={{ width: LABEL_W }} className="p-2 text-xs border-r border-border shrink-0 cursor-pointer"
                              onClick={() => {
                                if (r.kind === "task") setOpenTaskId(r.raw_id);
                                else {
                                  const found = milestones.find((m) => m.id === r.raw_id);
                                  if (found) setOpenMilestone(found);
                                }
                              }}>
                        <div className="flex items-center gap-1 font-medium truncate">
                          {r.kind === "milestone" ? <Flag className="w-3 h-3 text-sky-600 shrink-0" /> :
                            r.overdue ? <AlertTriangle className="w-3 h-3 text-rose-600 shrink-0" /> :
                            r.status === "done" ? <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" /> :
                            <span className="w-3 h-3 inline-block shrink-0" />}
                          <span className="truncate">{r.title}</span>
                        </div>
                        {groupBy !== "project" && <div className="text-[10px] text-muted-foreground truncate">{r.project_name}</div>}
                      </div>
                      <div className="relative h-8 bg-neutral-50/60 flex-1"
                              style={{ minWidth: timelinePx }}
                              onMouseEnter={(e) => setHover({ bar: r, x: e.clientX, y: e.clientY })}
                              onMouseMove={(e) => setHover((h) => (h && h.bar.id === r.id ? { ...h, x: e.clientX, y: e.clientY } : h))}
                              onMouseLeave={() => setHover((h) => (h && h.bar.id === r.id ? null : h))}>
                        {primaryTicks.map((t, i) => {
                          const tLeft = px(_iso(t.start));
                          const tW = Math.max(1, px(_iso(t.end)) - tLeft);
                          return (
                            <div key={`g-${i}`}
                                    className="absolute top-0 bottom-0 border-r border-border/40"
                                    style={{ left: tLeft, width: tW }} />
                          );
                        })}
                        {todayLeft != null && (
                          <div className="absolute top-0 bottom-0 border-l-2 border-primary/60"
                                  style={{ left: todayLeft }} title="Today" />
                        )}
                        <div
                          data-drag-bar={r.id}
                          className={`absolute top-1 h-6 border shadow-sm ${barCls} ${r.kind === "task" ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"} ${dragState?.bar?.id === r.id ? "ring-2 ring-primary" : ""}`}
                          style={{ left, width }}
                          onMouseDown={(e) => {
                            if (r.kind === "task" && e.button === 0) {
                              dragBar({ bar: r, mode: "move", e, rowEl: e.currentTarget.parentElement });
                            }
                          }}
                          onClick={(e) => {
                            if (dragState) return;
                            e.stopPropagation();
                            if (r.kind === "task") setOpenTaskId(r.raw_id);
                            else {
                              const found = milestones.find((m) => m.id === r.raw_id);
                              if (found) setOpenMilestone(found);
                              else nav(`/app/projects/${r.project_id}?tab=milestones`);
                            }
                          }}
                        >
                          {r.kind === "task" && width > 12 && (
                            <div className="absolute top-0 right-0 h-full w-1.5 cursor-ew-resize opacity-40 hover:opacity-100 bg-primary"
                                    onMouseDown={(e) => { e.stopPropagation(); dragBar({ bar: r, mode: "end", e, rowEl: e.currentTarget.parentElement.parentElement }); }}
                                    title="Drag right edge to change due date" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                };
                if (groupBy !== "project") return bars.map((r) => renderBarRow(r));
                const out = [];
                for (const g of grouped || []) {
                  const collapsed = collapsedGroups.has(g.project_id);
                  const gLeft = px(g.start);
                  const gWidth = Math.max(2, px(g.end) - gLeft);
                  out.push(
                    <div key={`group-${g.project_id}`}
                            className="flex items-stretch border-b bg-muted/30 sticky-group cursor-pointer"
                            onClick={() => toggleGroup(g.project_id)}
                            data-testid={`gantt-group-${g.project_id}`}>
                      <div style={{ width: LABEL_W }} className="p-2 text-xs border-r border-border shrink-0 flex items-center gap-1 font-semibold">
                        {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        <span className="truncate">{g.project_name}</span>
                        <span className="text-[10px] text-muted-foreground ml-1">· {g.total} bars</span>
                        {g.overdue > 0 && <span className="text-[10px] text-rose-700 font-bold ml-auto">{g.overdue} overdue</span>}
                      </div>
                      <div className="relative h-8 flex-1" style={{ minWidth: timelinePx }}>
                        {todayLeft != null && (
                          <div className="absolute top-0 bottom-0 border-l-2 border-primary/60"
                                  style={{ left: todayLeft }} />
                        )}
                        <div className="absolute top-1.5 h-5 border border-primary/60 bg-primary/10"
                                style={{ left: gLeft, width: gWidth }}
                                title={`${g.start} → ${g.end}`} />
                      </div>
                    </div>
                  );
                  if (!collapsed) {
                    for (const r of g.bars) out.push(renderBarRow(r, `${g.project_id}::`));
                  }
                }
                return out;
              })()}
            </div>
          </div>
          {/* Smart hover card — portal-style using fixed position. */}
          {hover && (
            <GanttHoverCard bar={hover.bar} x={hover.x} y={hover.y} />
          )}
        </div>
      )}
      {/* R138w · Full task-edit dialog + milestone peek. */}
      {openTaskId && (
        <PortfolioTaskEditDialog
          taskId={openTaskId}
          projectName={tasks.find((t) => t.id === openTaskId)?.project_name}
          onClose={() => setOpenTaskId(null)}
        />
      )}
      {openMilestone && (
        <MilestonePeekDialog milestone={openMilestone}
                                          onClose={() => setOpenMilestone(null)}
                                          onOpenInProject={() => nav(`/app/projects/${openMilestone.project_id}?tab=milestones`)} />
      )}
    </div>
  );
}


// R138y · Smart hover card floating next to the cursor. Renders bar
// details (dates · project · assignee · priority · status) so directors
// can inspect items without clicking through.
function GanttHoverCard({ bar, x, y }) {
  const startDate = new Date(bar.start);
  const endDate = new Date(bar.end);
  const durationDays = Math.max(1, Math.round((endDate - startDate) / 86400000) + 1);
  // Position tooltip to avoid running off-screen (offset by 14px).
  const style = {
    position: "fixed",
    left: Math.min(x + 14, (typeof window !== "undefined" ? window.innerWidth : 1200) - 280),
    top: Math.min(y + 14, (typeof window !== "undefined" ? window.innerHeight : 800) - 200),
    zIndex: 71,
  };
  const statusColor = bar.overdue ? "text-rose-700"
    : bar.status === "done" ? "text-emerald-700"
    : bar.kind === "milestone" ? "text-sky-700"
    : "text-indigo-700";
  return (
    <div style={style}
             className="w-72 border border-border bg-background shadow-xl p-3 pointer-events-none"
             data-testid="gantt-hover-card">
      <div className="flex items-start gap-2 mb-2">
        {bar.kind === "milestone" ? <Flag className="w-3.5 h-3.5 text-sky-600 mt-0.5" /> :
          bar.overdue ? <AlertTriangle className="w-3.5 h-3.5 text-rose-600 mt-0.5" /> :
          bar.status === "done" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5" /> :
          <span className="w-3.5 h-3.5 inline-block mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold leading-tight">{bar.title}</div>
          {bar.project_name && (
            <div className="text-[10px] text-muted-foreground mt-0.5">{bar.project_name}</div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Start</div>
          <div className="tabular-nums">{bar.start}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground">End</div>
          <div className={`tabular-nums ${bar.overdue ? "text-rose-700 font-semibold" : ""}`}>{bar.end}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Duration</div>
          <div className="tabular-nums">{durationDays}d</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Status</div>
          <div className={`uppercase tracking-wider ${statusColor}`}>{(bar.status || "").replace("_", " ")}</div>
        </div>
        {bar.assignee_name && (
          <div className="col-span-2">
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Assignee</div>
            <div>{bar.assignee_name}</div>
          </div>
        )}
        {bar.priority && (
          <div className="col-span-2">
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Priority</div>
            <div className="uppercase tracking-wider">{bar.priority}</div>
          </div>
        )}
      </div>
      <div className="text-[9px] text-muted-foreground mt-2 pt-2 border-t border-border/50">
        Click to {bar.kind === "task" ? "edit task" : "edit milestone"}
      </div>
    </div>
  );
}

// ── Task Peek row (embedded in the Projects tab expandable) ───────
export function TaskPeekPanel({ portfolioId, projectId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/portfolios/${portfolioId}/projects/${projectId}/task-peek?limit=5`);
        if (!cancel) setItems(data.items || []);
      } catch (e) { if (!cancel) { setItems([]); toast.error(apiErrorMessage(e, "Task peek failed")); } }
    })();
    return () => { cancel = true; };
  }, [portfolioId, projectId]);

  if (items === null) return <div className="p-2 text-[11px] text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading task peek…</div>;
  if (!items.length) return <div className="p-2 text-[11px] text-muted-foreground">No open tasks in this project.</div>;
  return (
    <div className="p-2 space-y-1" data-testid={`task-peek-${projectId}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Top open tasks</div>
      {items.map((t) => (
        <div key={t.id} className="flex items-center gap-2 text-[11px] py-0.5" data-testid={`task-peek-row-${t.id}`}>
          {t._overdue ? <AlertTriangle className="w-3 h-3 text-rose-600" /> : <span className="w-3 h-3 inline-block" />}
          <span className="flex-1 truncate">{t.title}</span>
          <span className="text-muted-foreground">{t.assignee_name || "unassigned"}</span>
          <span className={`tabular-nums text-[10px] ${t._overdue ? "text-rose-700 font-semibold" : "text-muted-foreground"}`}>{t.due_date || "—"}</span>
          {statusPill(t.status)}
        </div>
      ))}
    </div>
  );
}

// ── Quick Comment dialog ──────────────────────────────────────────
export function QuickCommentDialog({ portfolioId, project, open, onOpenChange, onPosted }) {
  const [text, setText] = useState("");
  const [visibility, setVisibility] = useState("internal");
  const [busy, setBusy] = useState(false);

  const post = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/portfolios/${portfolioId}/projects/${project?.id}/quick-comment`, {
        text: text.trim(), visibility,
      });
      toast.success("Comment posted to project activity feed");
      onPosted?.();
      setText("");
      onOpenChange?.(false);
    } catch (e) { toast.error(apiErrorMessage(e, "Post failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quick-comment-dialog">
        <DialogHeader>
          <DialogTitle>Quick comment · {project?.name}</DialogTitle>
          <DialogDescription>Post an internal status update or client-visible comment to this project&apos;s activity feed without leaving the portfolio view.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Post a status update, decision, or ask a question…" className="rounded-none text-sm" data-testid="quick-comment-text" autoFocus />
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Visibility</div>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger className="h-8 w-36 rounded-none text-xs" data-testid="quick-comment-visibility"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="internal">Internal</SelectItem>
                <SelectItem value="client">Client-visible</SelectItem>
              </SelectContent>
            </Select>
            <a href={`/app/projects/${project?.id}`} target="_blank" rel="noreferrer" className="ml-auto text-[10px] text-muted-foreground inline-flex items-center gap-1 hover:text-foreground">
              Open project <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange?.(false)} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={post} disabled={busy || !text.trim()} data-testid="quick-comment-post">
            {busy ? "Posting…" : "Post to activity feed"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
